/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { useNavigate } from 'react-router-dom';

const Logo = () => {
  const mountRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene, Camera, Renderer
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.x = 35;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    mountRef.current.appendChild(renderer.domElement);

    // Raycaster and Mouse
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let clickableObjects = [];

    // Torus Object
    const torusGeometry = new THREE.TorusGeometry(15, 1, 30, 200);
    const torusMaterial = new THREE.MeshBasicMaterial({ color: 'rgba(5, 13, 92, 0.918)' });
    const torus = new THREE.Mesh(torusGeometry, torusMaterial);
    scene.add(torus);
    clickableObjects.push(torus);

    torus.rotation.set(
      THREE.MathUtils.degToRad(90),
      THREE.MathUtils.degToRad(90),
      THREE.MathUtils.degToRad(90)
    );

    // Load Font and Create Text
    const fontLoader = new FontLoader();
    fontLoader.load('https://threejs.org/examples/fonts/helvetiker_regular.typeface.json', (font) => {
      const textGeometry = new TextGeometry('NIAD', {
        font: font,
        depth: 1,
        size: 7,
        height: 4,
        curveSegments: 20,
        bevelThickness: 0.1,
        bevelSize: 0.1,
        bevelOffset: 0,
        bevelSegments: 1,
      });

      const textMaterial = new THREE.MeshBasicMaterial({ color: 'rgba(5, 13, 92, 0.918)' });
      const textMesh = new THREE.Mesh(textGeometry, textMaterial);

      textGeometry.center();
      textMesh.rotation.y = THREE.MathUtils.degToRad(90);
      textMesh.position.set(0, 0, 0);

      scene.add(textMesh);
      clickableObjects.push(textMesh);
    });

    // Orbit Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.autoRotate = true;

    // Handle Window Resize
    const handleResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    // Click Event for Navigation
    const handleClick = (event) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(clickableObjects);

      if (intersects.length > 0) {
        navigate('/project');
      }
    };
    window.addEventListener('click', handleClick);

    // Mouse Move Event for Cursor Change
    const handleMouseMove = (event) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(clickableObjects);

      if (intersects.length > 0) {
        renderer.domElement.style.cursor = 'pointer';
      } else {
        renderer.domElement.style.cursor = 'default';
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);

    // Render Loop
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();

    // Cleanup Function
    return () => {
      if (mountRef.current) {
        mountRef.current.innerHTML = ''; // Clears the div safely
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('mousemove', handleMouseMove);
      controls.dispose();
      renderer.dispose();
    };
  }, [navigate]);

  return <div ref={mountRef} />;
};

export default Logo;